import { MutableRefObject, useEffect, useState } from 'react';
import { useClickOutside } from './useClickOutside';
import { InputSource } from '../types/InputSource';

/**
 * Handles the state for a searchbar's dropdown.  It will search when the minimum number of characters are entered,
 * then will hide if a user presses 'escape' or clicks outside the region.  Re-typing in the box re-enables the input
 *
 * Input Source Awareness:
 * The dropdown will only show when input comes from the keyboard or paste operations.
 * Dialpad button clicks will NOT trigger the dropdown, matching Webex App behavior.
 *
 * @param {string} input input string
 * @param {number} minInputLength minimum length of input string to start searching
 * @param {InputSource} inputSource the source of the input ('keyboard', 'dialpad', or 'paste')
 * @returns {[MutableRefObject, boolean]} array containing ref to assign to the root node and boolean on whether to show the dropdown
 */
export function useSearchDropdown<T extends HTMLElement>(
  input: string,
  minInputLength: number,
  inputSource: InputSource = 'keyboard'
): [MutableRefObject<T | null>, boolean] {
  const [isOpen, setIsOpen] = useState(false);
  const [ref] = useClickOutside<T>(() => setIsOpen(false), true);

  useEffect(() => {
    // Only show dropdown for keyboard and paste inputs, NOT for dialpad clicks
    // This matches the Webex App dialpad behavior
    const isSearchTriggeringSource = inputSource === 'keyboard' || inputSource === 'paste';
    const hasMinimumLength = input.length >= minInputLength;
    
    // Dropdown should only open when:
    // 1. Input has minimum required length
    // 2. Input source is keyboard or paste (NOT dialpad)
    const shouldOpen = hasMinimumLength && isSearchTriggeringSource;
    
    setIsOpen(shouldOpen);
  }, [input, minInputLength, inputSource]);

  return [ref, isOpen];
}
